import { useCallback, useEffect, useMemo, useState } from 'react';
import { AssetImageThumb } from './AssetImageThumb';
import { useBookingListAssetThumbs } from '../hooks/useBookingListAssetThumbs';
import {
  bookingRecordAssetId,
  bookingRecordAssetName,
  bookingRecordDateRangeLabel,
  bookingRecordIsHourly,
  bookingRecordScheduleLabel,
  bookingRowThumbSrc,
} from '../utils/bookingListDisplay';
import {
  bookingRecordCanMutate,
  bookingRecordMutateBlockReason,
  bookingRecordPrimaryId,
  extractBookingIdFromApiPayload,
  filterActiveMyBookingsListRecords,
  filterMyBookingsByListTimeFilter,
  type BookingListTimeFilter,
} from '../utils/bookingRecordMutate';
import {
  bookingAddonDisplayRows,
  bookingDetailFieldLabel,
  formatBookingDetailFieldValue,
  shouldSkipBookingDetailField,
} from '../utils/bookingDetailDisplay';
import { normalizeBookingListRecords } from '../utils/bookingListNormalize';
import { useI18n } from '../i18n';
import type { TranslateFn } from '../i18n/operationLabels';
import styles from './BookingResponseView.module.css';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function bookingRowId(row: Record<string, unknown>): string | null {
  return bookingRecordPrimaryId(row) ?? extractBookingIdFromApiPayload(row);
}

const TOP_FIELD_ORDER = [
  'id',
  'booking_id',
  'asset_id',
  'asset_name',
  'user_id',
  'client_id',
  'client_name',
  'owner_id',
  'status_name',
  'status',
  'booking_date',
  'start_date',
  'end_date',
  'start_time',
  'end_time',
  'total_price',
  'amount_of_people',
  'created_at',
  'updated_at',
  'cancelled_at',
];

function sortedDetailKeys(obj: Record<string, unknown>): string[] {
  const keys = Object.keys(obj);
  const primary = TOP_FIELD_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !TOP_FIELD_ORDER.includes(k));
  rest.sort();
  return [...primary, ...rest];
}

export { normalizeBookingListRecords };

export function isExplicitlyEmptyBookingList(data: unknown): boolean {
  if (Array.isArray(data)) {
    return data.length === 0;
  }
  if (isPlainObject(data)) {
    const inner = data.bookings ?? data.data ?? data.items ?? data.results;
    if (Array.isArray(inner)) {
      return inner.length === 0;
    }
  }
  return false;
}

function BookingListPanel({
  listRecords,
  responseLabelId,
  listDetail,
  onSelectRow,
  onBackToList,
  onUpdateBooking,
  onCancelBooking,
  onViewAsset,
  onViewConfirmation,
  cancellingBookingId,
  theme = 'dark',
}: {
  listRecords: Record<string, unknown>[];
  responseLabelId?: string;
  listDetail: Record<string, unknown> | null;
  onSelectRow: (row: Record<string, unknown>) => void;
  onBackToList: () => void;
  onUpdateBooking?: (row: Record<string, unknown>) => void;
  onCancelBooking?: (row: Record<string, unknown>) => void | Promise<void>;
  onViewAsset?: (row: Record<string, unknown>) => void;
  onViewConfirmation?: (row: Record<string, unknown>) => void;
  cancellingBookingId?: string | null;
  theme?: 'dark' | 'light';
}) {
  const { t, language } = useI18n();
  const thumbByAssetId = useBookingListAssetThumbs(listRecords);
  const showListActions = Boolean(
    onUpdateBooking || onCancelBooking || onViewAsset || onViewConfirmation,
  );

  const onActivateRow = useCallback(
    (row: Record<string, unknown>) => {
      if (onViewAsset && bookingRecordAssetId(row)) {
        onViewAsset(row);
        return;
      }
      onSelectRow(row);
    },
    [onSelectRow, onViewAsset],
  );

  if (listDetail) {
    const detailId = bookingRowId(listDetail);
    const mayMutate = bookingRecordCanMutate(listDetail);
    const cancelBusy = Boolean(detailId && cancellingBookingId === detailId);
    return (
      <>
        <div className={styles.listDetailToolbar}>
          <button type="button" className={styles.backToListButton} onClick={onBackToList}>
            {t('bookings.backToList')}
          </button>
          {showListActions ? (
            <div className={styles.listDetailActions}>
              {onViewConfirmation && bookingRowId(listDetail) ? (
                <button
                  type="button"
                  className={styles.viewConfirmationButton}
                  onClick={() => onViewConfirmation(listDetail)}
                >
                  {t('bookings.viewConfirmation')}
                </button>
              ) : null}
              {onViewAsset && bookingRecordAssetId(listDetail) ? (
                <button
                  type="button"
                  className={styles.viewAssetButton}
                  onClick={() => onViewAsset(listDetail)}
                >
                  {t('bookings.viewAsset')}
                </button>
              ) : null}
              {onUpdateBooking ? (
                <button
                  type="button"
                  className={styles.updateRowButton}
                  disabled={!mayMutate}
                  title={bookingUpdateBlockedTitle(listDetail, t)}
                  onClick={() => {
                    if (mayMutate) {
                      onUpdateBooking(listDetail);
                    }
                  }}
                >
                  {t('common.update')}
                </button>
              ) : null}
              {onCancelBooking ? (
                <button
                  type="button"
                  className={styles.cancelRowButton}
                  disabled={cancelBusy || !mayMutate}
                  title={bookingCancelBlockedTitle(listDetail, t)}
                  onClick={() => {
                    if (mayMutate && !cancelBusy) {
                      void onCancelBooking(listDetail);
                    }
                  }}
                >
                  {cancelBusy ? '…' : t('common.cancel')}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <BookingRecordView data={listDetail} theme={theme} />
      </>
    );
  }

  return (
    <div className={theme === 'light' ? styles.lightTheme : undefined}>
      <ul className={styles.list} aria-labelledby={responseLabelId}>
        {listRecords.map((row, index) => {
          const assetName = bookingRecordAssetName(row);
          const dateRange = bookingRecordDateRangeLabel(row, language);
          const thumbSrc = bookingRowThumbSrc(row, thumbByAssetId);
          const rowId = bookingRowId(row);
          const mayMutate = bookingRecordCanMutate(row);
          const cancelBusy = Boolean(rowId && cancellingBookingId === rowId);
          return (
            <li key={stableBookingKey(row, index)} className={styles.item}>
              <div className={styles.itemRow}>
                <button type="button" className={styles.rowButton} onClick={() => onActivateRow(row)}>
                  <BookingListRowThumbnail thumbSrc={thumbSrc} />
                  <span className={styles.rowMain}>
                    <span className={styles.name}>{assetName}</span>
                    <span className={styles.dateLine}>{dateRange}</span>
                  </span>
                </button>
                {showListActions ? (
                  <div className={styles.rowActions}>
                    {onViewConfirmation && rowId ? (
                      <button
                        type="button"
                        className={styles.viewConfirmationButton}
                        aria-label={t('bookings.viewConfirmationAria', { name: assetName })}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewConfirmation(row);
                        }}
                      >
                        {t('bookings.viewConfirmation')}
                      </button>
                    ) : null}
                    {onViewAsset && bookingRecordAssetId(row) ? (
                      <button
                        type="button"
                        className={styles.viewAssetButton}
                        aria-label={t('bookings.viewAssetAria', { name: assetName })}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewAsset(row);
                        }}
                      >
                        {t('bookings.viewAsset')}
                      </button>
                    ) : null}
                    {onUpdateBooking ? (
                      <button
                        type="button"
                        className={styles.updateRowButton}
                        disabled={!mayMutate}
                        aria-label={
                          mayMutate
                            ? t('bookings.list.updateAria', { name: assetName })
                            : bookingUpdateBlockedAria(row, assetName, t)
                        }
                        title={bookingUpdateBlockedTitle(row, t)}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (mayMutate) {
                            onUpdateBooking(row);
                          }
                        }}
                      >
                        {t('common.update')}
                      </button>
                    ) : null}
                    {onCancelBooking ? (
                      <button
                        type="button"
                        className={styles.cancelRowButton}
                        disabled={cancelBusy || !mayMutate}
                        aria-label={
                          mayMutate
                            ? t('bookings.list.cancelAria', { name: assetName })
                            : bookingCancelBlockedAria(row, assetName, t)
                        }
                        title={bookingCancelBlockedTitle(row, t)}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (mayMutate && !cancelBusy) {
                            void onCancelBooking(row);
                          }
                        }}
                      >
                        {cancelBusy ? '…' : t('common.cancel')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function bookingUpdateBlockedTitle(
  booking: Record<string, unknown>,
  t: TranslateFn,
): string | undefined {
  const reason = bookingRecordMutateBlockReason(booking);
  if (reason === 'past') {
    return t('bookings.list.pastUpdateOnly');
  }
  if (reason !== 'none') {
    return t('bookings.list.pendingUpdateOnly');
  }
  return undefined;
}

function bookingCancelBlockedTitle(
  booking: Record<string, unknown>,
  t: TranslateFn,
): string | undefined {
  const reason = bookingRecordMutateBlockReason(booking);
  if (reason === 'past') {
    return t('bookings.list.pastCancelOnly');
  }
  if (reason !== 'none') {
    return t('bookings.list.pendingCancelOnly');
  }
  return undefined;
}

function bookingUpdateBlockedAria(
  booking: Record<string, unknown>,
  name: string,
  t: TranslateFn,
): string {
  const reason = bookingRecordMutateBlockReason(booking);
  if (reason === 'past') {
    return t('bookings.list.updateBlockedPastAria', { name });
  }
  return t('bookings.list.updateBlockedAria', { name });
}

function bookingCancelBlockedAria(
  booking: Record<string, unknown>,
  name: string,
  t: TranslateFn,
): string {
  const reason = bookingRecordMutateBlockReason(booking);
  if (reason === 'past') {
    return t('bookings.list.cancelBlockedPastAria', { name });
  }
  return t('bookings.list.cancelBlockedAria', { name });
}

function stableBookingKey(booking: Record<string, unknown>, index: number): string {
  const id = booking.id ?? booking.booking_id;
  if (typeof id === 'string' && id.trim()) {
    return id.trim();
  }
  if (typeof id === 'number' && !Number.isNaN(id)) {
    return String(id);
  }
  return `idx-${index}`;
}

function BookingListRowThumbnail({ thumbSrc }: { thumbSrc: string | null }) {
  return (
    <span className={styles.thumbWrap}>
      <AssetImageThumb src={thumbSrc} className={styles.thumb} />
    </span>
  );
}

function BookingRecordView({
  data,
  theme = 'dark',
}: {
  data: Record<string, unknown>;
  theme?: 'dark' | 'light';
}) {
  const { language } = useI18n();
  const explicitTitle =
    (typeof data.title === 'string' && data.title.trim()) ||
    (typeof data.reference === 'string' && data.reference.trim());
  const title = explicitTitle || bookingRecordAssetName(data);

  const idShown =
    (typeof data.id === 'string' && data.id.trim()) ||
    (typeof data.booking_id === 'string' && data.booking_id.trim()) ||
    '';

  const keys = sortedDetailKeys(data);
  const scalarRows: { key: string; value: unknown }[] = [];

  for (const key of keys) {
    const v = data[key];
    if (shouldSkipBookingDetailField(key, v, data)) {
      continue;
    }
    if (v !== null && typeof v === 'object') {
      continue;
    }
    scalarRows.push({ key, value: v });
  }

  const addonRows = bookingAddonDisplayRows(data.addons, language);

  const scheduleLabel =
    typeof data.start_date === 'string' && typeof data.end_date === 'string'
      ? bookingRecordIsHourly(data)
        ? bookingRecordScheduleLabel(data, language)
        : bookingRecordDateRangeLabel(data, language)
      : null;

  return (
    <div className={`${styles.root}${theme === 'light' ? ` ${styles.lightTheme}` : ''}`}>
      <header className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {scheduleLabel ? <p className={styles.scheduleLine}>{scheduleLabel}</p> : null}
        {idShown ? (
          <p className={styles.idLine}>
            <span className={styles.idLabel}>ID</span>
            <code className={styles.idValue}>{idShown}</code>
          </p>
        ) : null}
      </header>

      {scalarRows.length > 0 ? (
        <section className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Details</h3>
          <dl className={styles.detailGrid}>
            {scalarRows.map(({ key, value }) => (
              <div key={key} className={styles.detailRow}>
                <dt>{bookingDetailFieldLabel(key)}</dt>
                <dd>{formatBookingDetailFieldValue(key, value, language)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {addonRows.length > 0 ? (
        <section className={styles.subsection}>
          <h3 className={styles.subsectionTitle}>Add-ons</h3>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Add-on</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {addonRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{row.quantity}</td>
                    <td>{row.price ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <details className={styles.rawDetails}>
        <summary>Raw JSON</summary>
        <pre className={styles.jsonFallback}>{JSON.stringify(data, null, 2)}</pre>
      </details>
    </div>
  );
}

type BookingResponseViewProps = {
  data: unknown;
  /** For accessibility — list is labelled by Assets-style response label. */
  responseLabelId?: string;
  /** When set, empty or unparseable list payloads show this instead of raw JSON. */
  listEmptyMessage?: string;
  /** List row / detail actions (e.g. My Bookings). */
  onUpdateBooking?: (row: Record<string, unknown>) => void;
  onCancelBooking?: (row: Record<string, unknown>) => void | Promise<void>;
  onViewAsset?: (row: Record<string, unknown>) => void;
  onViewConfirmation?: (row: Record<string, unknown>) => void;
  cancellingBookingId?: string | null;
  /** Hide cancelled/completed rows (My Bookings — API still returns them). */
  activeBookingsOnly?: boolean;
  /** When set with `activeBookingsOnly`, split list by whether `end_date` is in the past. */
  listTimeFilter?: BookingListTimeFilter;
  /** When set, open this booking's detail once the list rows are available. */
  initialDetailBookingId?: string | null;
  /** Called when the user leaves the auto-opened detail view. */
  onInitialDetailClosed?: () => void;
  /** Match mobile app white/blue pages. */
  theme?: 'dark' | 'light';
};

export function BookingResponseView({
  data,
  responseLabelId,
  listEmptyMessage,
  onUpdateBooking,
  onCancelBooking,
  onViewAsset,
  onViewConfirmation,
  cancellingBookingId = null,
  activeBookingsOnly = false,
  listTimeFilter = 'currentAndFuture',
  initialDetailBookingId = null,
  onInitialDetailClosed,
  theme = 'dark',
}: BookingResponseViewProps) {
  if (data === null) {
    return (
      <p className={styles.emptyList}>{listEmptyMessage ?? 'Empty response.'}</p>
    );
  }

  if (isPlainObject(data)) {
    return <BookingRecordView data={data} theme={theme} />;
  }

  return (
    <BookingMultiRecordView
      data={data}
      responseLabelId={responseLabelId}
      listEmptyMessage={listEmptyMessage}
      onUpdateBooking={onUpdateBooking}
      onCancelBooking={onCancelBooking}
      onViewAsset={onViewAsset}
      onViewConfirmation={onViewConfirmation}
      cancellingBookingId={cancellingBookingId}
      activeBookingsOnly={activeBookingsOnly}
      listTimeFilter={listTimeFilter}
      initialDetailBookingId={initialDetailBookingId}
      onInitialDetailClosed={onInitialDetailClosed}
      theme={theme}
    />
  );
}

function BookingMultiRecordView({
  data,
  responseLabelId,
  listEmptyMessage,
  onUpdateBooking,
  onCancelBooking,
  onViewAsset,
  onViewConfirmation,
  cancellingBookingId,
  activeBookingsOnly,
  listTimeFilter,
  initialDetailBookingId,
  onInitialDetailClosed,
  theme,
}: Omit<BookingResponseViewProps, 'data'> & { data: unknown }) {
  const [listDetail, setListDetail] = useState<Record<string, unknown> | null>(null);
  const resetKey = useMemo(() => {
    try {
      return JSON.stringify(data);
    } catch {
      return String(data);
    }
  }, [data]);

  const allListRecords = useMemo(() => normalizeBookingListRecords(data), [data]);
  const activeRecords = useMemo(
    () => (activeBookingsOnly ? filterActiveMyBookingsListRecords(allListRecords) : allListRecords),
    [activeBookingsOnly, allListRecords],
  );
  const listRecords = useMemo(
    () =>
      activeBookingsOnly && listTimeFilter
        ? filterMyBookingsByListTimeFilter(activeRecords, listTimeFilter)
        : activeRecords,
    [activeBookingsOnly, activeRecords, listTimeFilter],
  );

  useEffect(() => {
    setListDetail(null);
  }, [resetKey]);

  useEffect(() => {
    const wantId = initialDetailBookingId?.trim();
    if (!wantId || listRecords.length === 0) {
      return;
    }
    const wantNorm = wantId.toLowerCase();
    const match = listRecords.find((row) => {
      const id = bookingRowId(row);
      return id && id.toLowerCase() === wantNorm;
    });
    if (match) {
      setListDetail(match);
    }
  }, [initialDetailBookingId, listRecords, resetKey]);

  const onBackToList = useCallback(() => {
    setListDetail(null);
    if (initialDetailBookingId?.trim()) {
      onInitialDetailClosed?.();
    }
  }, [initialDetailBookingId, onInitialDetailClosed]);

  if (listRecords.length > 0) {
    return (
      <BookingListPanel
        listRecords={listRecords}
        responseLabelId={responseLabelId}
        listDetail={listDetail}
        onSelectRow={setListDetail}
        onBackToList={onBackToList}
        onUpdateBooking={onUpdateBooking}
        onCancelBooking={onCancelBooking}
        onViewAsset={onViewAsset}
        onViewConfirmation={onViewConfirmation}
        cancellingBookingId={cancellingBookingId}
        theme={theme}
      />
    );
  }

  if (listEmptyMessage && (allListRecords.length === 0 || activeBookingsOnly) && listRecords.length === 0) {
    return <p className={styles.emptyList}>{listEmptyMessage}</p>;
  }

  if (isExplicitlyEmptyBookingList(data)) {
    return <p className={styles.emptyList}>No bookings in the response.</p>;
  }

  return (
    <details className={styles.rawDetails}>
      <summary>Raw JSON</summary>
      <pre className={styles.fallbackPre}>
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}
